/**
 * GltfMissingFilesView
 *
 * Full-page view shown when a .gltf file is missing external resource files.
 * Lets the user provide the missing files so the upload can proceed.
 */

import { useState } from 'react';
import { ArrowBackIcon, ExclamationIcon, SuccessIcon } from '../../../icons';
import { UploadDropzone } from './UploadDropzone';
import { useMediaLibraryStore } from '../hooks/useMediaLibraryStore';

export interface GltfMissingFilesViewProps {
  /** ID of the upload associated with this .gltf file */
  uploadId: string;
  /** List of file names that are still missing */
  missingFiles: string[];
  /** Called with the files the user selected/dropped */
  onSubmit: (files: File[]) => void;
  /** Called when the user cancels */
  onClose: () => void;
}

const basename = (uri: string) => uri.split('/').pop() ?? uri;

export const GltfMissingFilesView = ({
  uploadId,
  missingFiles,
  onSubmit,
  onClose,
}: GltfMissingFilesViewProps) => {
  const uploadQueue = useMediaLibraryStore(s => s.uploadQueue);
  const [addedFiles, setAddedFiles] = useState<File[]>([]);

  const resolvedNames = new Set(addedFiles.map((f) => f.name));
  // Match by basename so "textures/foo.png" is resolved by dropping "foo.png"
  const stillMissing = missingFiles.filter((uri) => !resolvedNames.has(basename(uri)));
  const allResolved = stillMissing.length === 0;

  const expectedBasenames = new Set(missingFiles.map(basename));

  const handleDrop = (fileList: FileList) => {
    const incoming = Array.from(fileList).filter((f) => expectedBasenames.has(f.name));
    setAddedFiles((prev) => {
      const existing = new Map(prev.map((f) => [f.name, f]));
      for (const f of incoming) existing.set(f.name, f);
      return Array.from(existing.values());
    });
  };

  const handleSubmit = () => {
    if (allResolved) onSubmit(addedFiles);
  };

  const handleClose = () => {
    uploadQueue?.removeUpload(uploadId)
    onClose();
  }

  return (
    <div className="fullscreen-view gltf-missing-files-view">
      <header className="fullscreen-view__header">
        <button
          className="btn btn-ghost p0"
          onClick={handleClose}
          aria-label="Go back"
        >
          <ArrowBackIcon />
          Back
        </button>
      </header>

      <div className="fullscreen-view__body">
        <p className="fullscreen-view__subtitle">
          Locate the missing files and upload them to continue (max size: 20 MB)
        </p>

        <div className="gltf-missing-files-view__list-section">
          <ul className="gltf-missing-files-view__file-list">
            {addedFiles.length > 0 && (
              <ul className="gltf-missing-files-view__file-list">
                {addedFiles.map((file) => (
                  <li key={file.name} className="gltf-missing-files-view__file-item">
                    <SuccessIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
                    <span>
                      {file.name}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {stillMissing.map((name) => (
              <li key={name} className="gltf-missing-files-view__file-item gltf-missing-files-view__missing-item">
                <ExclamationIcon />
                {name}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <UploadDropzone onUpload={handleDrop} />
        </div>
        <div className="gltf-missing-files-view__actions">
          <button className="btn btn-small btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn btn-small btn-primary"
            onClick={handleSubmit}
            disabled={!allResolved}
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};
